/**
 * Patient personal information card
 * Refined design with golden accents
 */

import { User, Phone, Mail, MapPin, Calendar } from 'lucide-react'
import { formatDateCzech } from '@/lib/calendar-utils'
import type { PatientDetails } from '@/types/patient'

interface PatientInfoProps {
	patient: PatientDetails
}

export function PatientInfo({ patient }: PatientInfoProps) {
	return (
		<div className="rounded-xl bg-white p-6 shadow-sm border border-[#e8e4df]">
			{/* Header */}
			<div className="mb-6 flex items-start gap-4">
				<div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#f5ebe0] to-[#e8dfd0]">
					<User className="h-8 w-8 text-[#d7ad6c]" />
				</div>
				<div>
					<h1 className="text-2xl font-bold text-[#3d3428]">
						{patient.lastName} {patient.firstName}
						{patient.maidenName && (
							<span className="ml-2 text-lg font-normal text-[#8c8275]">
								(roz. {patient.maidenName})
							</span>
						)}
					</h1>
					<p className="mt-1 font-mono text-sm text-[#a09689]">{patient.personalId}</p>
				</div>
			</div>

			{/* Contact Info */}
			<div className="grid gap-3 md:grid-cols-2">
				{patient.phone && (
					<InfoRow
						icon={<Phone className="h-4 w-4" />}
						label="Telefon"
						value={patient.phone}
						href={`tel:${patient.phone}`}
					/>
				)}
				{patient.email && (
					<InfoRow
						icon={<Mail className="h-4 w-4" />}
						label="E-mail"
						value={patient.email}
						href={`mailto:${patient.email}`}
					/>
				)}
				{patient.address && (
					<InfoRow
						icon={<MapPin className="h-4 w-4" />}
						label="Adresa"
						value={patient.address}
					/>
				)}
				<InfoRow
					icon={<Calendar className="h-4 w-4" />}
					label="V systému od"
					value={formatDateCzech(new Date(patient.createdAt), 'd. MMMM yyyy')}
				/>
			</div>
		</div>
	)
}

interface InfoRowProps {
	icon: React.ReactNode
	label: string
	value: string
	href?: string
}

function InfoRow({ icon, label, value, href }: InfoRowProps) {
	const content = (
		<span className={href ? 'text-[#d7ad6c] hover:text-[#c49b5a] hover:underline transition-colors' : 'text-[#3d3428]'}>
			{value}
		</span>
	)

	return (
		<div className="flex items-center gap-3 rounded-xl bg-[#faf8f5] border border-[#f0ede8] p-3">
			<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#f5ebe0] text-[#a09689]">
				{icon}
			</div>
			<div>
				<p className="text-xs text-[#a09689]">{label}</p>
				{href ? <a href={href}>{content}</a> : content}
			</div>
		</div>
	)
}
